import { useCallback, useRef } from 'react'

/**
 * FLAG:useDocuAi — „Erneut analysieren" TAKES ITS RESULT OVER.
 *
 * The click is explicit: the reviewer asked for exactly these new values, so the server adopts them
 * when the run reaches its terminal (`analyzeFile`: `adoptNextAnalysis` → `metaData.analysisAdoptedAt`,
 * for a reviewed row through the same path as „In Prüfung zurücksetzen"). The F3 guard stays in place
 * for UNSOLICITED repeats — a redelivered event, a DocuAI-side reprocess — which must never overwrite
 * a review nobody asked to replace.
 *
 * The consequence for the hosts: on the terminal event they may no longer refresh the file metadata
 * only. The form still holds the values the run just replaced (and a deleted position list, being
 * `[]` rather than null, would otherwise permanently outlive every new analysis), so the review has
 * to be RESEEDED from the server. That is the one case in which discarding unsaved input is correct,
 * and the confirmation dialog says so before the run starts.
 *
 * One helper for all three review hosts (review page, drawer tab, embedded review) so they cannot
 * answer the "was this adopted?" question differently.
 */
export const useAdoptedAnalysis = () => {
  const seenAdoptedAtRef = useRef<string | null>(null)

  // Call with the file the form was seeded FROM — everything newer than this is an adoption the
  // reviewer has not seen yet.
  const rememberSeeded = useCallback((file?: { metaData?: { analysisAdoptedAt?: string | null } | null } | null) => {
    seenAdoptedAtRef.current = file?.metaData?.analysisAdoptedAt ?? null
  }, [])

  const hasAdoptedNewerAnalysis = useCallback((file?: { metaData?: { analysisAdoptedAt?: string | null } | null } | null) => {
    return (file?.metaData?.analysisAdoptedAt ?? null) !== seenAdoptedAtRef.current
  }, [])

  return { rememberSeeded, hasAdoptedNewerAnalysis }
}

export default useAdoptedAnalysis
